import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import { useDebouncedValue } from './useDebouncedValue';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/solid';
import { UserContext } from '../../providers/UserProvider';
import TemplateSelector from './TemplateSelector';
import { designsCollection, getValueWithoutUnit, checkUser, COLORS, FUENTES } from '../../utils';
import NameCard from './NameCard';

const inputStyles =
    'placeholder:text-neutral-300 mt-1 p-2 px-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50';
const defaults = {};

const Admin = (props) => {
    const { title } = props;
    let params = useParams();
    let navigate = useNavigate();
    const user = useContext(UserContext);
    const isValidUser = checkUser(user);
    const [nStickers, setNStickers] = useState(1);
    const [image, setImage] = useState('');
    const [thumbnail, setThumbnail] = useState('');
    const [category, setCategory] = useState('');
    const [coords, setCoords] = useState({});
    const [productUrl, setProductUrl] = useState('');
    const [designName, setDesignName] = useState('');
    const [defaultColor, setDefaultColor] = useState('FFFFFF');
    const [colorList, setColorList] = useState(COLORS);
    const [fontList, setFontList] = useState(FUENTES);
    const [defaultTypography, setDefaultTypography] = useState('Acumin');
    const [defaultAlignment, setDefaultAlignment] = useState('center');
    const [hideColorSelector, setHideColorSelector] = useState(false);
    const [hideFontSelector, setHideFontSelector] = useState(false);
    const [selectColorList, setSelectColorList] = useState(false);
    const [selectFontList, setSelectFontList] = useState(false);
    const [designSaved, setDesignSaved] = useState(false);
    const [showNotice, setShowNotice] = useState(false);
    const [designId, setDesignId] = useState(null);
    const [apply, setApply] = useState(false);
    const [templateApplied, setTemplateApplied] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [maxLength1, setMaxLength1] = useState();
    const [maxLength2, setMaxLength2] = useState();
    const debouncedMaxLength1 = useDebouncedValue(maxLength1, 200);
    const debouncedMaxLength2 = useDebouncedValue(maxLength2, 200);

    const saveButton = useRef();
    const prevCoords = useRef({});
    const coordStyles = useRef([]);
    const colorOptions = useRef(
        COLORS.map((color) => ({
            ...color,
            value: color.hex,
            label: color.name,
        }))
    );

    const fontOptions = useRef(
        FUENTES.map((fuente) => ({
            ...fuente,
            value: fuente.value,
            label: fuente.name,
        }))
    );

    const handleColorListSelection = (selected) => {
        const availableColorList = selected.map((color) => ({
            name: color.name,
            hex: color.hex,
        }));
        setColorList(availableColorList);
    };

    const handleFontListSelection = (selected) => {
        const availableFontList = selected.map((font) => ({
            name: font.name,
            value: font.value,
        }));
        setFontList(availableFontList);
    };

    const onChange = (evt) => {
        let position = evt.target.id.split('-');
        position = position[position.length - 1];

        switch (evt.target.name) {
            case 'productUrl':
                setProductUrl(evt.target.value);
                break;
            case 'name':
                setDesignName(evt.target.value);
                break;
            case 'color':
                setDefaultColor(evt.target.value);
                break;
            case 'typography':
                setDefaultTypography(evt.target.value);
                break;
            case 'alignment':
                setDefaultAlignment(evt.target.value);
                break;
            case 'maxlength1':
                setMaxLength1(evt.target.value);
                break;
            case 'maxlength2':
                setMaxLength2(evt.target.value);
                break;
            case 'hideColorSelector':
                setHideColorSelector(evt.target.checked);
                break;
            case 'hideFontSelector':
                setHideFontSelector(evt.target.checked);
                break;
            case 'selectColorList':
                setSelectColorList(evt.target.checked);
                break;
            case 'selectFontList':
                setSelectFontList(evt.target.checked);
                break;
            case 'designImage':
                setImage(evt.target.value);
                break;
            case 'designThumbnail':
                setThumbnail(evt.target.value);
                break;
            case 'category':
                setCategory(evt.target.value);
                break;
            case 'label-width':
                if (coordStyles.current[position]) {
                    const top = coordStyles.current[position].top.split('px');
                    const left = coordStyles.current[position].left.split('px');
                    const fontSize = coordStyles.current[position].fontSize.split('px');
                    setCoords({
                        position: Number(position),
                        width: evt.target.value,
                        top: top[0],
                        left: left[0],
                        fontSize: fontSize[0],
                    });
                } else {
                    setCoords({
                        position: Number(position),
                        width: evt.target.value,
                        top: prevCoords.current.top,
                        left: prevCoords.current.left,
                        fontSize: prevCoords.current.fontSize,
                    });
                }
                prevCoords.current.width = evt.target.value;
                break;
            case 'font-size':
                if (coordStyles.current[position]) {
                    const top = coordStyles.current[position].top.split('px');
                    const left = coordStyles.current[position].left.split('px');
                    const width = coordStyles.current[position].width.split('px');
                    setCoords({
                        position: Number(position),
                        width: width[0],
                        fontSize: Number(evt.target.value),
                        top: top[0],
                        left: left[0],
                    });
                } else {
                    setCoords({
                        position: Number(position),
                        width: prevCoords.current.width,
                        fontSize: Number(evt.target.value),
                        top: prevCoords.current.top,
                        left: prevCoords.current.left,
                    });
                }
                prevCoords.current.fontSize = evt.target.value;
                break;
            case 'coord-top':
                if (coordStyles.current[position]) {
                    const left = coordStyles.current[position].left.split('px');
                    const width = coordStyles.current[position].width.split('px');
                    const fontSize = coordStyles.current[position].fontSize.split('px');
                    setCoords({
                        position: Number(position),
                        top: evt.target.value,
                        left: left[0],
                        width: width[0],
                        fontSize: fontSize[0],
                    });
                } else {
                    setCoords({
                        position: Number(position),
                        top: evt.target.value,
                        left: prevCoords.current.left,
                        fontSize: prevCoords.current.fontSize,
                    });
                }
                prevCoords.current.top = evt.target.value;
                break;
            case 'coord-left':
                if (coordStyles.current[position]) {
                    const top = coordStyles.current[position].top.split('px');
                    const width = coordStyles.current[position].width.split('px');
                    const fontSize = coordStyles.current[position].fontSize.split('px');
                    setCoords({
                        position: Number(position),
                        top: top[0],
                        left: evt.target.value,
                        width: width[0],
                        fontSize: fontSize[0],
                    });
                } else {
                    setCoords({
                        position: Number(position),
                        top: prevCoords.current.top,
                        left: evt.target.value,
                        fontSize: prevCoords.current.fontSize,
                    });
                }
                prevCoords.current.left = evt.target.value;
                break;

            default:
                return false;
        }
    };

    const removeLastSticker = (evt) => {
        var ev2 = new Event('input', { bubbles: true });
        ev2.simulated = true;
        // document.querySelector('form:last-child input').value = 'With simulated flag';
        document.getElementById('label-width-0').dispatchEvent(ev2);

        setNStickers(Number(nStickers - 1));
    };

    const removeAllStickers = (evt) => {
        setNStickers(Number(0));
        coordStyles.current = [];
        setTemplateApplied(false);
    };

    const addSticker = (evt) => {
        setNStickers(Number(nStickers + 1));
    };

    const addCoords = useCallback(() => {
        [...Array(nStickers)].forEach((e, i) => {
            if (coords?.position === i) {
                coordStyles.current[i] = {
                    position: i,
                    top: `${coords.top || 0}px`,
                    left: `${coords.left || 0}px`,
                    width: `${coords.width || 0}px`,
                    fontSize: `${coords.fontSize || 14}px`,
                };
            }
        });
    }, [coords, nStickers]);

    const saveDesign = async (e) => {
        e.preventDefault();
        if (saveButton.current) saveButton.current.setAttribute('disabled', 'disabled');
        if (!designSaved && !params.designId) {
            const newDesign = {
                name: designName,
                labels: coordStyles.current,
                image,
                product: Number(productUrl),
                category,
                thumbnail,
                defaultColor,
                defaultTypography,
                defaultAlignment,
                maxLength1,
                maxLength2,
                hideColorSelector,
                hideFontSelector,
                active: true,
            };

            if (!maxLength1) {
                newDesign.maxLength1 = 100;
            }

            if (!maxLength2) {
                newDesign.maxLength2 = 100;
            }

            if (selectColorList && colorList.length > 0) {
                newDesign.availableColorList = [...colorList];
            }

            if (selectFontList && fontList.length > 0) {
                newDesign.availableFontList = [...fontList];
            }

            const designAdded = await addDoc(collection(db, designsCollection), newDesign);
            console.info('Design saved as: ', designAdded.id, newDesign);
            setDesignSaved(true);
            setShowNotice({ type: 'update', value: true });
            setTimeout(() => {
                setShowNotice({ type: 'update', value: false });
            }, 3000);
            saveButton.current.removeAttribute('disabled');
            navigate(`/admin/create`);
            setTimeout(() => {
                if (window.confirm(`Se ha guardado el diseño con el código: ${designAdded.id}`)) {
                    window.location.reload(false);
                }
            }, 1000);
        } else {
            console.log('Editing...', designId);
            updateDesign();
        }
    };

    const updateDesign = async () => {
        if (!designId) return;
        setShowNotice({ type: 'update', value: false });
        const updatedDesign = {
            name: designName,
            labels: coordStyles.current,
            image,
            product: Number(productUrl),
            category,
            thumbnail,
            defaultColor,
            defaultTypography,
            defaultAlignment,
            maxLength1,
            maxLength2,
            hideColorSelector,
            hideFontSelector,
        };

        if (selectColorList && colorList.length > 0) {
            updatedDesign.availableColorList = [...colorList];
        }

        if (selectFontList && fontList.length > 0) {
            updatedDesign.availableFontList = [...fontList];
        }

        const q = doc(db, designsCollection, designId);
        const designSnap = await getDoc(q);
        if (designSnap.exists()) {
            const designRef = doc(db, designsCollection, designId);
            await updateDoc(designRef, updatedDesign);
        }

        setDesignSaved(true);
        setShowNotice({ type: 'update', value: true });
        setTimeout(() => {
            setShowNotice({ type: 'update', value: false });
        }, 3000);
        console.log('Design edited.');
        saveButton.current.removeAttribute('disabled');
    };

    const populateDesign = useCallback(async () => {
        console.log('Populating design');
        setShowNotice({ type: 'loading', value: true });
        const q = doc(db, designsCollection, params.designId);
        const designSnap = await getDoc(q);

        if (designSnap.exists()) {
            const nStickers = designSnap.data().labels.length;
            setDesignId(params.designId);
            setImage(designSnap.data().image);
            setThumbnail(designSnap.data().thumbnail);
            setDesignName(designSnap.data().name);
            setDefaultColor(designSnap.data().defaultColor);
            setDefaultTypography(designSnap.data().defaultTypography);
            setDefaultAlignment(designSnap.data().defaultAlignment);
            setHideColorSelector(designSnap.data().hideColorSelector);
            setHideFontSelector(designSnap.data().hideFontSelector);
            setSelectColorList(designSnap.data().availableColorList.length);
            setColorList(designSnap.data().availableColorList ?? []);
            setSelectFontList(designSnap.data().availableFontList.length);
            setFontList(designSnap.data().availableFontList ?? []);
            setProductUrl(designSnap.data().product);
            setCategory(designSnap.data().category);
            setNStickers(nStickers);
            setMaxLength1(designSnap.data().maxLength1);
            setMaxLength2(designSnap.data().maxLength2);

            designSnap.data().labels.forEach((sticker, i) => {
                coordStyles.current.push({
                    top: sticker.top,
                    left: sticker.left,
                    width: sticker.width,
                    fontSize: sticker.fontSize,
                    position: sticker.position,
                });
            });
        } else {
            console.log('Design not found!');
            setShowNotice({ type: 'loading', value: false });
            navigate(`/admin`);
        }
        setTimeout(() => {
            setShowNotice({ type: 'loading', value: false });
        }, 2000);
    }, [params, navigate]);

    const applyChanges = () => {
        setApply(!apply);
    };

    const applyTemplate = (templateCoords) => {
        if (!templateCoords) return;
        setNStickers(templateCoords.length);
        templateCoords.forEach((coord) => {
            coordStyles.current.push(coord);
        });
        setTemplateApplied(true);
    };

    useEffect(() => {
        addCoords();
    }, [coords, nStickers, addCoords]);

    useEffect(() => {
        if (!params.designId) return;
        setDesignId(params.designId);
        populateDesign();
    }, [params.designId, populateDesign]);

    const isScrolledIntoView = (el) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const elemTop = rect.top;
        const elemBottom = rect.bottom;

        // const isVisible = (elemTop >= 0) && (elemBottom <= window.innerHeight);
        // Partially visible elements return true:
        const isVisible = elemTop < window.innerHeight && elemBottom >= 0;
        return isVisible;
    };
    useEffect(() => {
        document.title = title;
        window.addEventListener('scroll', (e) => {
            const elem = document.querySelectorAll('.inView')[0];
            setScrolled(!isScrolledIntoView(elem));
        });
    }, [title, scrolled]);

    const handleDeletion = () => {
        if (window.confirm('¿Está seguro?')) {
            removeAllStickers();
        }
    };

    useEffect(() => {
        defaults.color = defaultColor;
        defaults.typography = defaultTypography;
        defaults.alignment = defaultAlignment;
    }, [defaultColor, defaultTypography, defaultAlignment]);

    return (
        <>
            {isValidUser ? (
                <>
                    <div>
                        <section className="bg-neutral-700 p-5">
                            <div className="container grid grid-cols-3">
                                <Link
                                    to={'/admin/list'}
                                    className="w-30 z-40 rounded-md hover:bg-etiquette bg-gray-500 text-white font-bold px-4 py-2 mr-auto">
                                    Lista de diseños
                                </Link>
                                <h1 className="text-2xl text-gray-300 font-medium text-center uppercase mt-1">
                                    {params.designId ? 'Editando: ' : 'Nuevo Diseño: '}
                                    {}
                                    {designName}
                                    {designsCollection === 'designs_dev' && (
                                        <span className="text-xs normal-case absolute right-1/2 top-0">
                                            {designsCollection}
                                        </span>
                                    )}
                                </h1>
                                <Link
                                    to={'/admin/create'}
                                    className="z-40 rounded-md hover:bg-etiquette bg-gray-500 text-white font-bold px-4 py-2 ml-auto">
                                    Nuevo
                                </Link>
                            </div>
                        </section>

                        <div
                            className={`relative p-4 bg-green-300 ${
                                showNotice.value === true ? 'opacity-90' : 'hidden opacity-0'
                            } w-full transition-opacity duration-500 ease-in-out shadow-md z-30`}>
                            <p className="text-green-800 text-center">
                                {showNotice.type === 'loading' ? 'Cargando el diseño' : 'Diseño guardado correctamente'}
                            </p>
                        </div>

                        {/* Design form */}
                        <section className="bg-gray-100 py-8">
                            <div className="container grid grid-cols-5 gap-6">
                                <div className="mb-4">
                                    <label className="text-sm text-gray-600 font-semibold mb-2">
                                        Ruta de la miniatura del diseño:
                                    </label>
                                    <input
                                        type="text"
                                        onChange={onChange}
                                        name="designThumbnail"
                                        className={inputStyles}
                                        value={thumbnail}
                                        placeholder="Imagen miniatura del diseño"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="text-sm text-gray-600 font-semibold mb-2">
                                        Ruta de imagen para este diseño:
                                    </label>
                                    <input
                                        type="text"
                                        onChange={onChange}
                                        name="designImage"
                                        className={inputStyles}
                                        value={image}
                                        placeholder="URL de la imagen"
                                    />
                                </div>
                                <div className="relative w-full mb-4 inView">
                                    <a
                                        className="absolute -top-0 right-2 text-xs hover:text-blue-500 hover:underline"
                                        href="https://cln.sh/D0jbS3"
                                        target="_blank"
                                        rel="noreferrer"
                                        title="¿Cómo encontrarlo?">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth={1.5}
                                            stroke="currentColor"
                                            className="w-6 h-6">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                                            />
                                        </svg>
                                    </a>
                                    <label className="text-sm text-gray-600 font-semibold mb-2">
                                        Producto en WordPress:
                                    </label>
                                    <input
                                        type="number"
                                        onChange={onChange}
                                        id={`productUrl-0`}
                                        name={`productUrl`}
                                        value={productUrl}
                                        className={inputStyles}
                                        placeholder="Código numérico del producto, ej: 200019"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="text-sm text-gray-600 font-semibold mb-2">
                                        Nombre para el diseño:
                                    </label>
                                    <input
                                        type="text"
                                        onChange={onChange}
                                        id={`name-0`}
                                        name={`name`}
                                        value={designName}
                                        className={inputStyles}
                                        placeholder="Por ejemplo Piratas, Fútbol, Estrella, etc"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="text-sm text-gray-600 font-semibold mb-2">
                                        Categoría para el diseño:
                                    </label>
                                    <input
                                        type="text"
                                        onChange={onChange}
                                        id={`category-0`}
                                        name={`category`}
                                        value={category}
                                        className={inputStyles}
                                        placeholder="Por ejemplo: Niño, Niña, Teen Niño, etc"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Painting area */}
                        <section className="container">
                            <div className="lg:grid grid-cols-2 gap-4">
                                <div className="relative mb-6 max-w-2xl bg-neutral-100 mt-8">
                                    <div
                                        className={`fixed ${
                                            scrolled ? 'top-10' : ''
                                        } rounded-lg overflow-hidden mx-12 my-8 shadow-lg`}>
                                        <div className="absolute z-0 left-0 top-0 w-40 h-1"></div>
                                        {nStickers &&
                                            [...Array(nStickers)].map((e, i) => {
                                                const extraStyling = {};
                                                if (coordStyles.current[i]) {
                                                    const fontSize = coordStyles.current[i].fontSize?.split('px') || 14;
                                                    extraStyling.lh = fontSize[0] < 16 ? 'et-lh-13' : '';
                                                }
                                                return (
                                                    <NameCard
                                                        key={`name-card-${i}`}
                                                        id={i}
                                                        name={'Nombre Apellido'}
                                                        coords={coordStyles.current[i]}
                                                        defaults={defaults}
                                                    />
                                                );
                                            })}
                                        {image && (
                                            <>
                                                <img
                                                    className="z-0"
                                                    width="565"
                                                    height="565"
                                                    src={image}
                                                    alt="Previsualiza"
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="relative bg-white z-10 p-8 pt-0 border-l-8 border-gray-100 pb-20">
                                    {/* Opciones */}
                                    <div>
                                        <h4 className="text-lg text-gray-600 mt-8 mb-4">Opciones:</h4>
                                        <hr className="my-6" />
                                        <div>
                                            <label className="block text text-gray-600 block my-4 mt-6">
                                                Color del texto por defecto:
                                            </label>
                                            {COLORS.map((color) => {
                                                const { hex } = color;
                                                return (
                                                    <button
                                                        className={`w-8 h-8 m-0.5 text-black rounded-full border-2 hover:border-blue-500 palette_${hex} ${
                                                            defaultColor === hex
                                                                ? 'border-etiquette'
                                                                : 'border-gray-200'
                                                        }`}
                                                        key={hex}
                                                        onClick={onChange}
                                                        name="color"
                                                        value={hex}></button>
                                                );
                                            })}
                                            <div className={`mt-2 ${hideColorSelector ? 'mb-8' : 'mb-4'}`}>
                                                <input
                                                    id="hideColorSelector"
                                                    type="checkbox"
                                                    name="hideColorSelector"
                                                    className="inline-block mr-2 w-4 h-4 p-2 border-2 border-gray-200 outline-none cursor-pointer"
                                                    onChange={onChange}
                                                />
                                                <label
                                                    htmlFor="hideColorSelector"
                                                    className="inline-block text-sm text-gray-600 font-semibold">
                                                    ¿Deshabilitar selección de color al cliente?
                                                </label>
                                            </div>
                                            {!hideColorSelector && (
                                                <>
                                                    <div className={`mt-2 ${!selectColorList ? 'mb-8' : 'mb-4'}`}>
                                                        <input
                                                            id="selectColorList"
                                                            type="checkbox"
                                                            name="selectColorList"
                                                            className="inline-block mr-2 w-4 h-4 p-2 border-2 border-gray-200 outline-none cursor-pointer"
                                                            onChange={onChange}
                                                            checked={params.designId && selectColorList}
                                                        />
                                                        <label
                                                            htmlFor="selectColorList"
                                                            className="inline-block text-sm text-gray-600 font-semibold">
                                                            ¿Limitar colores disponibles?
                                                        </label>
                                                    </div>

                                                    {selectColorList && (
                                                        <>
                                                            {params.designId && (
                                                                <>
                                                                    <h4 className="text-sm text-gray-500">
                                                                        Colores seleccionados actualmente:
                                                                    </h4>
                                                                    <ul className="mt-2 mb-4">
                                                                        {colorList.map((color) => {
                                                                            return (
                                                                                <li
                                                                                    key={color.hex}
                                                                                    className={`text-sm font-medium bg-gray-200 rounded p-1 px-2 inline m-1`}>
                                                                                    {color.name}
                                                                                </li>
                                                                            );
                                                                        })}
                                                                    </ul>
                                                                </>
                                                            )}

                                                            <Select
                                                                isMulti
                                                                id="colorList"
                                                                name="colorList"
                                                                options={colorOptions.current}
                                                                className="basic-multi-select mb-8"
                                                                classNamePrefix="select"
                                                                onChange={handleColorListSelection}
                                                                placeholder="Selecciona los colores disponibles para este diseño"
                                                            />
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <hr className="my-6" />
                                        <div>
                                            <label className="block text-sm text-gray-600 font-semibold my-2">
                                                Tipografía por defecto:
                                            </label>
                                            <div className="grid grid-cols-5 gap-2">
                                                {FUENTES.map((fuente) => {
                                                    const { name, value } = fuente;
                                                    return (
                                                        <button
                                                            className={`rounded-lg h-16 bg-gray-100 hover:bg-etiquette hover:text-white text-gray-500 uppercase text-xs border-2 py-2 font_${value} ${
                                                                defaultTypography === value
                                                                    ? 'border-etiquette'
                                                                    : 'border-gray-100'
                                                            } ${name === 'Sacramento' ? 'px-1' : 'px-3'}`}
                                                            key={value}
                                                            onClick={onChange}
                                                            name="typography"
                                                            value={value}>
                                                            {name}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className={`mt-2 ${!selectColorList ? 'mb-8' : 'mb-4'}`}>
                                                <input
                                                    id="hideFontSelector"
                                                    type="checkbox"
                                                    name="hideFontSelector"
                                                    value={true}
                                                    className="inline-block mr-2 w-4 h-4 p-2 border-2 border-gray-200 outline-none cursor-pointer"
                                                    onChange={onChange}
                                                />
                                                <label
                                                    htmlFor="hideFontSelector"
                                                    className="inline-block text-sm text-gray-600 font-semibold">
                                                    ¿Deshabilitar selección de tipografía al cliente?
                                                </label>
                                            </div>
                                            {!hideFontSelector && (
                                                <>
                                                    <div className={`mt-2 ${!selectFontList ? 'mb-8' : 'mb-4'}`}>
                                                        <input
                                                            id="selectFontList"
                                                            type="checkbox"
                                                            name="selectFontList"
                                                            className="inline-block mr-2 w-4 h-4 p-2 border-2 border-gray-200 outline-none"
                                                            onChange={onChange}
                                                            checked={params.designId && selectFontList}
                                                        />
                                                        <label
                                                            htmlFor="selectFontList"
                                                            className="inline-block text-sm text-gray-600 font-semibold">
                                                            ¿Limitar tipografías disponibles?
                                                        </label>
                                                    </div>

                                                    {selectFontList && (
                                                        <>
                                                            {params.designId && (
                                                                <>
                                                                    <h4 className="text-sm text-gray-500">
                                                                        tipografías seleccionados actualmente:
                                                                    </h4>
                                                                    <ul className="mt-2 mb-4">
                                                                        {fontList.map((font) => {
                                                                            return (
                                                                                <li
                                                                                    key={font.value}
                                                                                    className={`text-sm font-medium bg-gray-200 rounded p-1 px-2 inline m-1`}>
                                                                                    {font.name}
                                                                                </li>
                                                                            );
                                                                        })}
                                                                    </ul>
                                                                </>
                                                            )}

                                                            <Select
                                                                isMulti
                                                                id="fontList"
                                                                name="fontList"
                                                                options={fontOptions.current}
                                                                className="basic-multi-select mb-8"
                                                                classNamePrefix="select"
                                                                onChange={handleFontListSelection}
                                                                placeholder="Selecciona las tipografías disponibles para este diseño"
                                                            />
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-2">
                                            {/* Alineación textos */}
                                            <div>
                                                <label className="block text text-gray-600 block my-4 mt-6">
                                                    Alineación del texto por defecto:
                                                </label>

                                                <div
                                                    className={`inline-block cursor-pointer m-1 text-black border-2 hover:border-blue-500 ${
                                                        defaultAlignment === 'left'
                                                            ? 'border-etiquette'
                                                            : 'border-gray-200'
                                                    }`}>
                                                    <input
                                                        id="alignmentleft"
                                                        type="radio"
                                                        className={`hidden w-8 h-8`}
                                                        onClick={onChange}
                                                        name="alignment"
                                                        value="left"
                                                    />

                                                    <label htmlFor="alignmentleft" className="cursor-pointer">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="w-6 h-6">
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12"
                                                            />
                                                        </svg>
                                                    </label>
                                                </div>
                                                <div
                                                    className={`inline-block cursor-pointer m-1 text-black border-2 hover:border-blue-500 ${
                                                        defaultAlignment === 'center'
                                                            ? 'border-etiquette'
                                                            : 'border-gray-200'
                                                    }`}>
                                                    <input
                                                        id="alignmentcenter"
                                                        className={`hidden w-8 h-8`}
                                                        onClick={onChange}
                                                        name="alignment"
                                                        value="center"
                                                        type="radio"
                                                    />
                                                    <label htmlFor="alignmentcenter" className="cursor-pointer">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="w-6 h-6">
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                                                            />
                                                        </svg>
                                                    </label>
                                                </div>
                                                <div
                                                    className={`inline-block cursor-pointer m-1 text-black border-2 hover:border-blue-500 ${
                                                        defaultAlignment === 'right'
                                                            ? 'border-etiquette'
                                                            : 'border-gray-200'
                                                    }`}>
                                                    <input
                                                        id="alignmentright"
                                                        className={`hidden w-8 h-8`}
                                                        onClick={onChange}
                                                        name="alignment"
                                                        value="right"
                                                        type="radio"
                                                    />
                                                    <label htmlFor="alignmentright" className="cursor-pointer">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="w-6 h-6">
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M3.75 6.75h16.5M3.75 12h16.5M12 17.25h8.25"
                                                            />
                                                        </svg>
                                                    </label>
                                                </div>
                                            </div>
                                            {/* Longitud de caracteres */}
                                            <div>
                                                <label className="block text text-gray-600 block my-4 mt-6">
                                                    Longitud máxima de caracteres
                                                </label>
                                                <div className="grid grid-cols-2 gap-6">
                                                    <div>
                                                        <h4 className="text-sm text-gray-500 font-semibold mb-2">
                                                            Primera línea:
                                                        </h4>
                                                        <input
                                                            className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                                            type="number"
                                                            name="maxlength1"
                                                            onChange={onChange}
                                                            value={debouncedMaxLength1 ?? 0}
                                                        />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm text-gray-500 font-semibold mb-2">
                                                            Segunda línea:
                                                        </h4>
                                                        <input
                                                            className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                                            type="number"
                                                            name="maxlength2"
                                                            value={debouncedMaxLength2 ?? 0}
                                                            onChange={onChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            className="transition duration-300 w-auto p-2 px-4 mt-6 bg-green-500 hover:bg-green-800  rounded-full text-sm shadow-md text-white"
                                            onClick={applyChanges}>
                                            <CheckIcon className="h-5 w-5 text-white inline" /> Aplicar cambios
                                        </button>
                                    </div>
                                    <hr className="my-6" />

                                    <h4 className="text-lg text-gray-600 my-8">Plantillas para coordenadas:</h4>
                                    <TemplateSelector applyTemplate={applyTemplate} />
                                    <hr className="mt-10 mb-6" />

                                    {/* Botones */}
                                    <div className="z-20 w-auto lg:grid grid-cols-5 gap-2 right-12 mb-8 mr-4">
                                        <button
                                            className="transition duration-300 bg-green-500 hover:bg-green-700 text-white rounded-full py-2 px-2 shadow-md"
                                            onClick={addSticker}>
                                            <PlusIcon className="float-left h-5 w-5 text-white" />
                                            <span className="inline">Nuevo</span>
                                        </button>
                                        {nStickers > 1 && (
                                            <>
                                                <button
                                                    className="transition duration-300 bg-red-500 hover:bg-red-700 text-white rounded-full py-2 px-4 shadow-md"
                                                    onClick={removeLastSticker}>
                                                    <TrashIcon className="float-left h-5 w-5 text-white" />
                                                    <span className="inline">Último</span>
                                                </button>
                                                <button
                                                    className="transition duration-300 bg-red-500 hover:bg-red-700 text-white rounded-full py-2 px-4 shadow-md"
                                                    onClick={handleDeletion}>
                                                    <TrashIcon className="float-left h-5 w-5 text-white" />
                                                    <span className="inline">Todos</span>
                                                </button>
                                            </>
                                        )}
                                        <div className="col-span-2"></div>
                                    </div>

                                    <hr className="mt-4 mb-8" />

                                    {/* Coordenadas */}

                                    {nStickers &&
                                        [...Array(nStickers)].map((e, i) => {
                                            return (
                                                <div
                                                    key={`stickerCoord-${i}`}
                                                    className="relative lg:grid grid-cols-4 gap-3 mb-6 pb-4 px-14 border-b-2 border-gray-100">
                                                    <span className="hidden lg:block absolute text-4xl text-gray-300 font-bold left-0 top-10">
                                                        {i + 1}·
                                                    </span>
                                                    {/* Ancho */}
                                                    <div className="mb-4">
                                                        <h4 className="text-l text-gray-500 font-semibold mb-2">
                                                            Ancho:
                                                        </h4>
                                                        {!params.designId && !templateApplied ? (
                                                            <input
                                                                type="number"
                                                                onChange={onChange}
                                                                id={`label-width-${i}`}
                                                                name={`label-width`}
                                                                className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                                            />
                                                        ) : (
                                                            <input
                                                                type="number"
                                                                onChange={onChange}
                                                                id={`label-width-${i}`}
                                                                name={`label-width`}
                                                                value={getValueWithoutUnit(
                                                                    coordStyles.current[i]?.width,
                                                                    'px'
                                                                )}
                                                                className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                                            />
                                                        )}
                                                    </div>
                                                    {/* Fuente */}
                                                    <div className="mb-4">
                                                        <h4 className="text-l text-gray-500 font-semibold mb-2">
                                                            Fuente:
                                                        </h4>
                                                        <select
                                                            onChange={onChange}
                                                            id={`font-size-${i}`}
                                                            name={`font-size`}
                                                            value={getValueWithoutUnit(
                                                                coordStyles.current[i]?.fontSize,
                                                                'px'
                                                            )}
                                                            className="mt-1 p-3 px-4 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                                                            <option value="12">12</option>
                                                            <option value="14">14</option>
                                                            <option value="16">16</option>
                                                            <option value="18">18</option>
                                                            <option value="20">20</option>
                                                            <option value="22">22</option>
                                                            <option value="24">24</option>
                                                            <option value="24">26</option>
                                                            <option value="24">28</option>
                                                            <option value="30">30</option>
                                                            <option value="32">32</option>
                                                            <option value="34">34</option>
                                                            <option value="36">36</option>
                                                        </select>
                                                    </div>
                                                    {/* Izquierda */}
                                                    <div className="mb-4">
                                                        <h4 className="text-l text-gray-500 font-semibold mb-2">
                                                            Izquierda:
                                                        </h4>
                                                        {!params.designId && !templateApplied ? (
                                                            <input
                                                                type="number"
                                                                onChange={onChange}
                                                                id={`coord-left-${i}`}
                                                                name={`coord-left`}
                                                                className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                                            />
                                                        ) : (
                                                            <input
                                                                type="number"
                                                                onChange={onChange}
                                                                id={`coord-left-${i}`}
                                                                name={`coord-left`}
                                                                value={getValueWithoutUnit(
                                                                    coordStyles.current[i]?.left,
                                                                    'px'
                                                                )}
                                                                className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                                            />
                                                        )}
                                                    </div>
                                                    {/* Arriba */}
                                                    <div>
                                                        <h4 className="text-l text-gray-500 font-semibold mb-2">
                                                            Arriba:
                                                        </h4>
                                                        {!params.designId && !templateApplied ? (
                                                            <input
                                                                type="number"
                                                                onChange={onChange}
                                                                id={`coord-top-${i}`}
                                                                name={`coord-top`}
                                                                className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                                            />
                                                        ) : (
                                                            <input
                                                                type="number"
                                                                onChange={onChange}
                                                                id={`coord-top-${i}`}
                                                                name={`coord-top`}
                                                                value={getValueWithoutUnit(
                                                                    coordStyles.current[i]?.top,
                                                                    'px'
                                                                )}
                                                                className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                                            />
                                                        )}
                                                    </div>
                                                    <button
                                                        className="absolute right-0 bottom-10 w-auto p-2 bg-green-300 hover:bg-green-500  rounded-md text-sm shadow-md"
                                                        onClick={applyChanges}>
                                                        <CheckIcon className="h-5 w-5 text-white" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    <button
                                        onClick={saveDesign}
                                        className="w-full rounded-md disabled:bg-gray-300 bg-blue-400 hover:bg-blue-500 text-white font-bold p-4"
                                        title="Aplicar"
                                        ref={saveButton}>
                                        Guardar Diseño
                                    </button>
                                </div>
                            </div>
                        </section>
                    </div>
                </>
            ) : (
                <div className="container mx-auto mt-20 text-center">
                    <h1 className="text-3xl">Permission denied</h1>
                    <Link
                        to="/login"
                        className="relative block text-center mx-auto mt-8 py-4 px-8 bg-green-500 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-opacity-75">
                        Login
                    </Link>
                </div>
            )}
        </>
    );
};
export default Admin;
